import { useMemo, useState } from 'react';

import { useReportedStreaming } from 'hooks/statekeeping';

import { CameraSources, StreamSource, StreamSourceFriendly } from 'lib/api/manuel-control';
import { Streaming_StreamSource_SourceType } from 'lib/generated/proto/roboverse_platform_protos/statekeeping/Reported/Streaming';

import { CameraSource, DefaultCameraSource, StreamSourceName } from './types';

export const DEFAULT_CAMERA_SOURCE: CameraSource = {
  value: DefaultCameraSource.OFF,
  label: 'Off',
};

/**
 * Old hook for old api - used for parsing where this is still used
 */
export function usePrimaryStreamSources() {
  const { streamSources } = useStreamSources();

  const cameraSources = useMemo(() => {
    const sources = CameraSources.getFriendlyPrimaryStreamSources(streamSources);
    return [DEFAULT_CAMERA_SOURCE].concat(
      sources.map((source) => ({
        value: source.name,
        label: source.label,
      })),
    );
  }, [streamSources]);

  return { cameraSources };
}

/**
 * todo: with migration to new state keeping we did not change everything to the new type - could be done :)
 */
export function useStreamSources() {
  const { data } = useReportedStreaming();

  const streamSources: StreamSource[] = data.availableStreamSources.map((stream) => ({
    ...stream,
    nativeResolution: {
      width: stream.nativeResolution?.width || 1280,
      height: stream.nativeResolution?.height || 720,
    },
  }));

  return {
    streamSources,
  };
}

export function useSpotStreamSources() {
  const { streamSources } = useStreamSources();

  const { ncbStreamSources, graspStreamSources } = useMemo(() => {
    const sources = streamSources
      .filter((s) => s.type === Streaming_StreamSource_SourceType.SPOT)
      .filter((s) => !!s.spotServiceName)
      .sort((a, b) => a.name.localeCompare(b.name));

    const ncbStreamSources = CameraSources.getFriendlyNCBStreamSources(sources);
    const graspStreamSources = CameraSources.getFriendlyGraspStreamSources(sources);
    return { ncbStreamSources, graspStreamSources };
  }, []);

  return { ncbStreamSources, graspStreamSources };
}

function useStreamSource(sources: StreamSourceFriendly[], defaultStream?: StreamSourceName) {
  const initialState: StreamSourceName =
    (defaultStream && sources.find((e) => e.name === defaultStream)?.name) ??
    sources[0]?.name ??
    'no stream source - should not not happen!';

  const [selectedStreamName, setSelectedStreamName] = useState<StreamSourceName>(initialState);

  function handleStreamSourceChange(value: StreamSourceName) {
    setSelectedStreamName(value);
  }

  const streamSource = useMemo(() => {
    return sources.find((s) => s.name === selectedStreamName)!;
  }, [selectedStreamName, sources]);

  return {
    selectedStreamSource: selectedStreamName,
    handleStreamSourceChange,
    sources,
    streamSource,
  };
}

export function useNcbStreamSource(defaultStream?: StreamSourceName) {
  const { ncbStreamSources } = useSpotStreamSources();
  return { ...useStreamSource(ncbStreamSources, defaultStream), ncbStreamSources };
}

export function useGraspStreamSource(defaultStream?: StreamSourceName) {
  const { graspStreamSources } = useSpotStreamSources();
  return { ...useStreamSource(graspStreamSources, defaultStream), graspStreamSources };
}
